import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Package, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['Received', 'Reviewing', 'Drop-off', 'Preparing', 'Live', 'Sold', 'Paid'];
const STAGE_STEP = {
  application_submitted: 0,
  under_vetting: 1,
  awaiting_dropoff: 2,
  agreement_sent: 2,
  awaiting_checkin: 2,
  in_preparation: 3,
  live_on_marketplace: 4,
  sold: 5,
  payout_issued: 6,
};
const STAGE_LABEL = {
  application_submitted: 'Application received',
  under_vetting: 'Being reviewed by our team',
  awaiting_dropoff: 'Booked for drop-off',
  agreement_sent: 'Agreement signed — awaiting drop-off',
  awaiting_checkin: 'Awaiting check-in',
  in_preparation: 'Getting ready to list',
  live_on_marketplace: 'Live on the marketplace',
  sold: 'Sold! Payout on the way',
  payout_issued: 'Sold & paid out',
};

const money = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function SellerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/${token}`);
      setData(res.data);
      setError(false);
    } catch (e) {
      setError(true);
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#F4F5F1] flex items-center justify-center p-6" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div className="text-center" data-testid="portal-not-found">
          <p className="text-2xl font-bold text-[#474747] mb-2">Link not found</p>
          <p className="text-sm text-gray-500">This tracking link isn't valid — give Listers a shout and we'll send you a fresh one.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F5F1]" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="max-w-2xl mx-auto px-4 py-10">
        <p className="text-3xl font-black text-[#1E2117] tracking-tight mb-1" data-testid="portal-brand">Listers<span className="text-[#8CA43C]">.</span></p>
        {data && (
          <>
            <h1 className="text-xl font-semibold text-[#474747]" data-testid="portal-greeting">G'day {data.seller_first_name} — here's how your items are going</h1>
            <p className="text-xs text-gray-400 mb-8">Updates automatically • You keep 70% of every sale</p>

            {data.entries.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E9E9E9] p-10 text-center text-gray-500 text-sm" data-testid="portal-empty">
                Nothing here yet — once you've got items with Listers they'll show up right here.
              </div>
            ) : (
              <div className="space-y-5">
                {data.entries.map((e, idx) => {
                  const step = STAGE_STEP[e.stage] ?? 0;
                  const isPaid = e.stage === 'payout_issued';
                  const isSold = e.stage === 'sold' || isPaid;
                  const price = e.listed_price || e.nominated_sale_price;
                  return (
                    <div key={e.id || `${e.title || 'entry'}-${idx}`} className="bg-white rounded-2xl border border-[#E9E9E9] p-5 shadow-sm" data-testid={`portal-item-${idx}`}>
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <Package className="w-5 h-5 text-[#8CA43C] shrink-0" />
                          <p className="text-base font-bold text-[#1E2117] truncate">{e.title}</p>
                        </div>
                        {isSold ? (
                          <div className="text-right shrink-0">
                            <p className="text-lg font-black text-[#1E2117]">{money(e.sale_price)}</p>
                            <p className="text-[11px] text-gray-400 -mt-0.5">sold price</p>
                          </div>
                        ) : price ? (
                          <div className="text-right shrink-0">
                            <p className="text-lg font-black text-[#1E2117]" data-testid={`portal-price-${idx}`}>{money(price)}</p>
                            <p className="text-[11px] text-gray-400 -mt-0.5">{e.listed_price ? 'current price' : 'your asking price'}</p>
                          </div>
                        ) : null}
                      </div>
                      <p className="text-sm text-[#5A7014] font-semibold mb-3" data-testid={`portal-status-${idx}`}>{STAGE_LABEL[e.stage] || e.stage}</p>

                      <div className="flex items-center gap-1 mb-1">
                        {STEPS.map((s, i) => (
                          <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-[#C6E76C]' : 'bg-gray-200'}`} />
                        ))}
                      </div>
                      <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                        <span>{STEPS[0]}</span>
                        <span className="font-semibold text-[#5A7014]">{STEPS[step]}</span>
                        <span>{STEPS[STEPS.length - 1]}</span>
                      </div>

                      {isSold && e.payout_amount && (
                        <div className={`mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${isPaid ? 'bg-[#F7FBEC] text-[#3D4237] border border-[#C6E76C]' : 'bg-amber-50 text-amber-800 border border-amber-200'}`} data-testid={`portal-payout-${idx}`}>
                          <CheckCircle2 className="w-4 h-4" />
                          {isPaid ? `Your payout of ${money(e.payout_amount)} has been sent` : `Your payout: ${money(e.payout_amount)} (70%) — on its way soon`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-center text-[11px] text-gray-400 mt-8">Questions? Just reply to any of our texts or give us a call. — Listers</p>
          </>
        )}
      </div>
    </div>
  );
}
